import { ApiError, getTRPCErrorDescription, isApiError } from "@platform/shared/errors";
import {
  Button,
  ButtonIcon,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  IconSprite,
  TooltipGroup,
} from "@platform/ui/components";
import { copy, heading } from "@platform/ui/styles";
import { TRPCClientError } from "@trpc/client";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { isRouteErrorResponse, useLocation, useRouteError } from "react-router";
import { twMerge } from "tailwind-merge";
import { isTRPCClientError } from "~/shared/api/core/trpc.utils";
import { NestedNotFound, NotFound } from "~/shared/components/errors/NotFound";
import { useCopyToClipboard } from "~/shared/hooks/useCopyToClipboard";
import { posthogClient } from "~/shared/lib/posthog/posthog-client";
import { asError, isWebpackChunkError } from "~/shared/util/errorHelpers";
import {
  ErrorDisplayActions,
  ErrorDisplayContent,
  ErrorDisplayHeadline,
  ErrorDisplayLockup,
  ErrorDisplayLogo,
  ErrorDisplayMessage,
  ErrorDisplayRoot,
} from "./ErrorDisplay";
import { GoBackHome } from "./GoBackHome";

export function TopLevelRouteErrorHandler() {
  const routeError = useRouteError();

  useCaptureRouteError(routeError);

  if (isRouteErrorResponse(routeError)) {
    return (
      <div className="content-container flex min-h-[calc(100dvh-var(--siteheader-height,0px))] items-center justify-center">
        <KnownRouteError error={routeError} />
      </div>
    );
  }

  const normalizedError = asError(routeError);
  return (
    <div className="content-container flex min-h-[calc(100dvh-var(--siteheader-height,0px))] items-center justify-center">
      <ErrorHandler error={normalizedError} />
    </div>
  );
}

export function NestedRouteErrorHandler() {
  const routeError = useRouteError();

  useCaptureRouteError(routeError);

  if (isRouteErrorResponse(routeError)) {
    return <KnownRouteError error={routeError} nested />;
  }

  const normalizedError = asError(routeError);
  return (
    <div className="flex w-full items-center justify-center py-10">
      <ErrorHandler error={normalizedError} />
    </div>
  );
}

function useCaptureRouteError(error: unknown) {
  React.useEffect(() => {
    if (isRouteErrorResponse(error)) {
      if (error.status >= 500) {
        posthogClient?.captureException(new Error(`Server Error ${error.status}: ${error.statusText || "Unknown"}`), {
          status: error.status,
          statusText: error.statusText,
        });
      }
    } else {
      posthogClient?.captureException(asError(error));
    }
  }, [error]);
}

function KnownRouteError({ error, nested = false }: { error: unknown; nested?: boolean }) {
  const isRouteError = isRouteErrorResponse(error);
  const translatedError = React.useMemo(() => {
    if (isRouteError) {
      return new Error(error.data ? error.data : `${error.status} ${error.statusText}`);
    }
    return new Error("An unknown error occurred");
  }, [error, isRouteError]);

  if (!isRouteError) {
    return <GeneralRouteErrorMessage error={translatedError} />;
  }

  switch (error.status) {
    case 404: {
      return nested ? <NestedNotFound /> : <NotFound />;
    }
    default: {
      return <GeneralRouteErrorMessage error={translatedError} />;
    }
  }
}

interface ErrorBoundaryMessageProps {
  error: Error;
}

function ErrorHandler({ error }: ErrorBoundaryMessageProps) {
  if (isWebpackChunkError(error)) {
    return <ChunkLoadErrorMessage />;
  }

  if (isTRPCClientError(error)) {
    switch (error.data?.code) {
      case "FORBIDDEN": {
        return <ForbiddenErrorMessage />;
      }
      case "TOO_MANY_REQUESTS": {
        return <RateLimitErrorMessage />;
      }
      case "NOT_FOUND": {
        return <NotFound />;
      }
    }
  }

  if (isApiError(error)) {
    switch (error.details.code) {
      case "FORBIDDEN": {
        return <ForbiddenErrorMessage />;
      }
      case "RATE_LIMITED": {
        return <RateLimitErrorMessage />;
      }
    }
  }

  return <GeneralRouteErrorMessage error={error} />;
}

function ForbiddenErrorMessage() {
  return (
    <div className="flex items-center justify-center">
      <ErrorDisplayRoot>
        <ErrorDisplayLogo />

        <ErrorDisplayContent>
          <ErrorDisplayLockup>
            <ErrorDisplayHeadline>You don't have access to this page</ErrorDisplayHeadline>
            <ErrorDisplayMessage>
              Sorry, you&apos;re signed in but don&apos;t have permission to view this content. Please contact support
              if you think this is a mistake.
            </ErrorDisplayMessage>
          </ErrorDisplayLockup>
          <ErrorDisplayActions>
            <GoBackHome />
          </ErrorDisplayActions>
        </ErrorDisplayContent>
      </ErrorDisplayRoot>
    </div>
  );
}

function RateLimitErrorMessage() {
  return (
    <div className="flex items-center justify-center">
      <ErrorDisplayRoot>
        <ErrorDisplayLogo />

        <ErrorDisplayContent>
          <ErrorDisplayLockup>
            <ErrorDisplayHeadline>Slow down a bit</ErrorDisplayHeadline>
            <ErrorDisplayMessage>
              You&apos;ve made too many requests too quickly. Please wait a moment before trying again, or come back in
              a few minutes.
            </ErrorDisplayMessage>
          </ErrorDisplayLockup>
          <ErrorDisplayActions>
            <Button size="lg" onClick={hardReload}>
              Retry
            </Button>
          </ErrorDisplayActions>
        </ErrorDisplayContent>
      </ErrorDisplayRoot>
    </div>
  );
}

function ChunkLoadErrorMessage() {
  return (
    <div className="stack-y-6 w-full max-w-prose">
      <div className="stack-y-4 items-center">
        <Icon className="size-8" symbol="loop-right-line" />
        <h1
          className={heading({
            variant: "24",
            align: "center",
            className: "md:text-3xl",
          })}
        >
          Our app has been updated
        </h1>

        <p
          className={copy({
            variant: "16",
            color: "secondary",
            align: "center",
            className: "text-balance",
          })}
        >
          The BreakLine app has been updated. Please refresh the page or click the button below to get the latest
          version.
        </p>
      </div>
      <div className="mx-auto">
        <Button size="lg" onClick={hardReload}>
          Reload
        </Button>
      </div>
    </div>
  );
}

function getGeneralErrorDetails(error: Error) {
  if (error instanceof ApiError) {
    return {
      errorHeadline: error.message,
      errorData: {
        errorType: "api",
        ...(error.details.response?.data ?? {
          statusCode: error.details.status,
          statusText: error.message,
        }),
      },
    };
  }

  if (isTRPCClientError(error)) {
    return {
      errorHeadline: getTRPCErrorDescription(error.data?.code),
      errorData: {
        errorType: "trpc",
        code: error.data?.code,
        httpStatus: error.data?.httpStatus,
      },
    };
  }

  return {
    errorHeadline: "Application Error",
    errorData: {
      errorType: "general",
      statusText: error.message,
    },
  };
}

function GeneralRouteErrorMessage({ error }: { error: Error }) {
  const { copy: copyFn, isCopying, hasCopied } = useCopyToClipboard();
  const { errorHeadline, errorData } = getGeneralErrorDetails(error);
  const { pathname } = useLocation();
  const errorMessage = {
    pathname,
    ...errorData,
  };
  const errorText = JSON.stringify(errorMessage, null, 2);

  return (
    <ErrorDisplayRoot>
      <ErrorDisplayLogo />

      <ErrorDisplayContent>
        <ErrorDisplayLockup>
          <ErrorDisplayHeadline>Something went wrong</ErrorDisplayHeadline>
          <ErrorDisplayMessage className="max-w-prose">
            We&apos;re having trouble loading this content right now. Please try reloading the page, or come back in a
            few minutes. If the issue continues, you can copy the details below and share them with support.
          </ErrorDisplayMessage>
        </ErrorDisplayLockup>
        <ErrorDisplayActions>
          <Button size="sm-to-lg" variant="outline" onClick={hardReload}>
            Reload
          </Button>
          <GoBackHome />
        </ErrorDisplayActions>
      </ErrorDisplayContent>

      <div
        className={twMerge(
          "mx-auto grid w-full max-w-[665px] gap-2.5 px-4 pt-3 pb-4 sm:px-6 sm:pt-4 sm:pb-6",
          "border-ds-stroke-primary bg-ds-surface-1 rounded-xl border",
        )}
      >
        <h2
          className={copy({
            variant: "14",
            color: "tertiary",
          })}
        >
          Error Details
        </h2>
        <Card className="w-full">
          <Collapsible>
            <div className="grid gap-4 p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm sm:text-base">{errorHeadline}</div>

                <TooltipGroup className="flex justify-end">
                  <CollapsibleTrigger
                    className="origin-center transition-transform data-[state=open]:rotate-180"
                    aria-label="View error details"
                    asChild
                  >
                    <Button variant="ghost" svgOnly size="icon">
                      <ButtonIcon symbol="arrow-down-s-line" />
                    </Button>
                  </CollapsibleTrigger>

                  <Button
                    aria-label="Copy error details"
                    onClick={async () => {
                      if (isCopying) return;
                      await copyFn(`\`\`\`${errorText}\`\`\``);
                    }}
                    svgOnly
                    variant="ghost"
                    size="icon"
                  >
                    <ButtonIcon symbol={!hasCopied ? "file-copy-line" : "checkbox-check"} />
                  </Button>
                </TooltipGroup>
              </div>

              <CollapsibleContent className="max-h-72 w-full max-w-[515px] overflow-scroll font-mono text-sm whitespace-pre-wrap">
                {errorText}
              </CollapsibleContent>
            </div>
          </Collapsible>
        </Card>
      </div>
    </ErrorDisplayRoot>
  );
}

export function RootError({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="flex min-h-dvh items-center justify-center">
          <ErrorHandler error={error} />
          <IconSprite />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export function EntityErrorHandler({
  renderNotFound,
}: {
  renderNotFound: (error: ApiError | TRPCClientError<any>) => React.ReactNode;
}) {
  const routeError = useRouteError();

  if (isApiError(routeError)) {
    if (routeError.details.code === "NOT_FOUND") {
      return (
        <div className="content-container flex min-h-[calc(100dvh-var(--siteheader-height))] items-center justify-center">
          {renderNotFound(routeError)}
        </div>
      );
    }
  }

  if (isTRPCClientError(routeError)) {
    if (routeError.data?.code === "NOT_FOUND") {
      return (
        <div className="content-container flex min-h-[calc(100dvh-var(--siteheader-height))] items-center justify-center">
          {renderNotFound(routeError)}
        </div>
      );
    }
  }

  return <TopLevelRouteErrorHandler />;
}

function hardReload(): void {
  window.location.reload();
}
